import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Clapperboard } from 'lucide-react';
import { editorLang, editorPath } from '@/components/editorPages/routes';
import { PAGE_ICONS, TOOL_PAGE_KEYS } from '@/components/editorPages/icons';
import { hasFinePointer, isSmallScreen } from '@/lib/motion';

// CHOPBOX ON THE LANDING PAGE (2026-09-29, owner: the editor needs its own
// ad, not a tile at the foot of the page). Two pieces:
//   ChopBoxVisual  the whole editor exactly as /editor opens: a screenshot of
//                  ChopBox on its own sample clip (rail, stage, Trim panel,
//                  timeline, Export) with "ChopBoX" stamped across the clip in
//                  black, tilted, cracking the glass. Owner: "this needs to be
//                  the image" —
//                  no photo of anyone, never cropped. Re-take it when the
//                  editor's look changes.
//   ChopBoxPitch   the compass's ChopBox stop: the pitch, Open ChopBox, and
//                  every tool as its own button straight into that tool.

const SUITE = '/promo/chopbox-editor-1600.webp';
const SUITE_SRCSET = '/promo/chopbox-editor-800.webp 800w, /promo/chopbox-editor-1600.webp 1600w';

/**
 * The suite is always shown whole. In the chooser card (card) it sits below
 * the NEW sticker so the sticker covers none of it; the compass stop's box
 * has the screenshot's own shape.
 */
export function ChopBoxVisual({ card = false, sizes = '(min-width: 640px) 52vw, 100vw' }: { card?: boolean; sizes?: string }) {
  return (
    <div aria-hidden="true" className="absolute inset-0 bg-[#0d1016]">
      <img
        src={SUITE}
        srcSet={SUITE_SRCSET}
        sizes={sizes}
        width={1600}
        height={965}
        alt=""
        loading="lazy"
        decoding="async"
        className={card ? 'absolute inset-x-3 bottom-3 top-14 h-[calc(100%-4.25rem)] w-[calc(100%-1.5rem)] object-contain' : 'h-full w-full object-contain'}
      />
    </div>
  );
}

export function ChopBoxPitch() {
  const { t, i18n } = useTranslation();
  const lang = editorLang(i18n.language);
  return (
    <motion.div
      // On a phone or touch screen the card is simply there (the storybook
      // pitch's rule: a busy phone held the fade-up at opacity 0).
      initial={isSmallScreen() || !hasFinePointer() ? false : { opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5 }}
      className="mx-auto max-w-6xl rounded-2xl border border-border/90 bg-card/75 p-1.5 text-left shadow-[var(--shadow-card)] sm:p-2"
      data-testid="chopbox-pitch"
    >
      <div className="relative overflow-hidden rounded-[1.1rem] bg-[linear-gradient(160deg,hsl(184_40%_14%),hsl(220_20%_8%)_55%)] p-6 max-[359px]:p-4 sm:p-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.05fr] lg:items-center">
          <div>
            <h2 className="text-balance text-[26px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground sm:text-[36px]">
              {t('chopboxPitch.heading')}
            </h2>
            <p className="mt-3 text-pretty text-[16px] leading-relaxed text-muted-foreground sm:text-[17px]">{t('chopboxPitch.body')}</p>
            <ul className="mt-4 flex flex-wrap gap-2">
              {(['free', 'noSignup', 'noWatermark', 'onDevice'] as const).map((chip) => (
                <li key={chip} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[13px] text-foreground">
                  <Check className="h-3.5 w-3.5 text-[#4FC5CD]" aria-hidden="true" />
                  {t(`editorPages.common.chips.${chip}`)}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to={editorPath('hub', lang)}
                data-testid="chopbox-pitch-open"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#4FC5CD] px-6 text-[15.5px] font-bold text-[#0b0d12] transition-transform hover:-translate-y-0.5"
              >
                {t('chopboxPitch.cta')}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to={editorPath('animatePhoto', lang)}
                data-testid="chopbox-pitch-animate"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-[#F4BE52]/50 px-6 text-[15.5px] font-bold text-[#F4BE52] transition-colors hover:bg-[#F4BE52]/10"
              >
                <Clapperboard className="h-4 w-4" aria-hidden="true" />
                {t('chopboxPitch.animateCta')}
              </Link>
            </div>
            <p className="mt-3 text-[13px] text-muted-foreground">{t('chopboxPitch.meta')}</p>
          </div>
          <div className="relative aspect-[1600/965] w-full overflow-hidden rounded-xl border border-white/10 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.9)]">
            <ChopBoxVisual sizes="(min-width: 1024px) 560px, 100vw" />
          </div>
        </div>

        {/* Every tool, its own button, straight into that tool in the editor. */}
        <nav className="mt-8 border-t border-white/10 pt-6" aria-labelledby="chopbox-pitch-tools">
          <h3 id="chopbox-pitch-tools" className="text-[15px] font-semibold text-foreground">
            {t('chopboxPitch.toolsTitle')}
          </h3>
          <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {TOOL_PAGE_KEYS.map((key) => {
              const Icon = PAGE_ICONS[key];
              return (
                <li key={key}>
                  <Link
                    to={editorPath(key, lang)}
                    data-testid={`chopbox-pitch-tool-${key}`}
                    className="flex min-h-[52px] items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[14px] font-medium text-foreground transition-colors hover:border-[#4FC5CD]/60 hover:text-[#4FC5CD]"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-[#4FC5CD]" aria-hidden="true" />
                    <span className="min-w-0">{t(`editorPages.pages.${key}.card`)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </motion.div>
  );
}
